/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  createContext,
  useContext,
  useEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import useSetState from 'hooks/useSetState';
import { interactionContext as InteractionContext } from './interactionContext';
import Id_ from './Message';
import collectFeedbackSubmitted from 'assets/collectFeedbackSubmitted.svg';
import { useDispatch } from 'react-redux';
import pubSub from 'services/pubSub';
import hexToRgb from 'services/hexToRGB';
import { colors } from 'styles/variables';
import { TextEntry } from 'components/UI/TextEntry';

import { HostingContext } from 'hosting/context';
import { feedbackEvent, textEntryValueEvent } from 'hosting/events';

const { lGrey2 } = colors;
const FiveStarRatingContext = createContext<any>(undefined);

const FeedbackSubmitted = ({ color }) => {
  return (
    <div
      className="fader_-5s"
      css={css`
        // position: absolute;
        display: flex;
        flex-direction: column;
        justify-content: center;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        margin: 30px 0 0 0;
      `}
    >
      <img src={collectFeedbackSubmitted} />
      <div
        css={css`
          margin: 50px 0 0 0;
          font-size: 18px;
          text-align: center;

          color: ${color};
        `}
        role="alert"
      >
        Thanks! Your feedback has been submitted.
        {/* {children} */}
      </div>
    </div>
  );
};

const FiveStarRating = props => {
  const { children, onSubmit = () => {} } = props;
  let [FiveStarList, Message, SubmitBtn, FeedbackSubmittedEditable] = children;

  const interactionContext = useContext(InteractionContext);
  const hostingCtx = useContext(HostingContext);

  const {
    useMyFeedbackApp = 0,
    emailRequired = 1,
    emailOptional = 0,
    anonymityRequired = 0,
    identityPlaceholder,
    include5StarRating = true,
    includeMessageText = true,
    messageTextBackgroundColor = '#fff',
    messageTextBackgroundOpacity = 100,
    messageBorderRadius = 0,
    reference = '',
    collectionKey = '',
    textColor,
    isTextEntry,
    messageTextColor = '#000',
    messageBorderColor = '#d2dff9',
    fontFamily = 'inherit',
    messageFontSize = 16,
  } = interactionContext.props;

  let ratingEndpoint = interactionContext.props?.ratingEndpoint;
  // ratingEndpoint will be undefined for modules before we
  // made the update to include hosting
  //
  if (!ratingEndpoint) {
    if (useMyFeedbackApp) {
      ratingEndpoint = 'collect';
    } else {
      ratingEndpoint = 'email';
    }
  }

  //
  const {
    state: { rating, learnerIdentity, message },
    setState,
  } = useSetState({
    rating: null,
    learnerIdentity: '',
    message: '',
  });

  const showTextEntry = isTextEntry || (!isTextEntry && includeMessageText);

  let {
    messageContainer,
    messageBorder = true,
    messageUnderline,
  } = interactionContext.props;

  if (!isTextEntry && !messageUnderline) {
    messageContainer = true;
  }

  if (isTextEntry && !messageContainer) {
    messageBorder = true;
    messageUnderline = true;
  }

  const [submitted, setSubmitted] = useState(false);

  let hasLearnerIdentity = learnerIdentity !== '';

  const dispatch = useDispatch();

  const { r, g, b } = hexToRgb(
    messageTextBackgroundColor === '#fff'
      ? '#ffffff'
      : messageTextBackgroundColor
  );
  const a = messageTextBackgroundOpacity / 100;

  const Id =
    ratingEndpoint === 'collect' && (!!emailRequired || !!emailOptional) ? (
      <Id_
        placeholder={identityPlaceholder || 'Enter your id'}
        onUpdateCallback="onUpdateLearnerIdentity"
        cssStyle={{
          height: 45,
          margin: '0 0 10px 0',
          overflow: 'hidden',
          color: messageTextColor,

          '::placeholder': {
            color: messageTextColor,
          },

          ...(!messageUnderline
            ? {
                background:
                  `rgba(${r}, ${g}, ${b}, ${a})` || messageTextBackgroundColor,
                border: messageBorder
                  ? `1px solid ${messageBorderColor}`
                  : 'none',
                borderRadius: messageBorderRadius,

                boxShadow: `0 0 0 0 ${messageBorderColor}`,
                transition: 'box-shadow 0.1s',

                '&:focus': {
                  outline: 'none !important',
                  border: 'none !important',

                  boxShadow: `0 0 0 3px ${
                    !messageBorder ? '#d2dff9' : messageBorderColor
                  }`,
                },
              }
            : {
                background: 'transparent',
                outline: 'none',
                border: 'none',

                borderRadius: 0,

                borderBottomColor: 'transparent',
                transition: 'all 0.2s',

                '&:focus': {
                  outline: 'none !important',
                  border: 'none !important',

                  borderBottomStyle: 'solid !important',
                  borderBottomWidth: '3px !important',
                  borderBottomColor: `${
                    !messageBorder ? '#d2dff9' : messageBorderColor
                  } !important`,
                },
              }),
        }}
        aria-autocomplete="given-name"
      />
    ) : null;

  useEffect(() => {
    pubSub.subscribe(
      `SHOW_HIDE_RATING_FEEDBACK_${interactionContext.id}`,
      ({ showSubmissionFeedback }) => {
        setSubmitted(showSubmissionFeedback);
      }
    );
  }, []);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    const feedback = {
      interactionName: interactionContext?.props?.name,

      message,
      rating,
    };

    if (isTextEntry) {
      hostingCtx?.data(
        textEntryValueEvent(interactionContext.id, {
          value: message,
        })
      );
    } else {
      hostingCtx?.data(feedbackEvent(interactionContext.id, feedback));
    }
  }, [submitted]);

  return (
    <FiveStarRatingContext.Provider
      value={{
        rating,
        message,
        hasLearnerIdentity,
        learnerIdentity,
        include5StarRating,
        onSubmit(ratingIsReporting?: boolean) {
          setSubmitted(true);
          onSubmit();

          // we're sending the 5 start rating as likert
          if (ratingIsReporting) {
            return;
          }

          dispatch({
            type: 'SUBMIT_FEEDBACK',
            data: {
              key: collectionKey,
              rating: rating === null || isTextEntry ? undefined : rating + 1,
              message,
              identity: learnerIdentity,
              reference,
            },
          });
        },
        alertLearnerIdentity() {},
        onUpdateRating(index) {
          setState({ rating: index === rating ? null : index });
        },
        onUpdateLearnerIdentity(learnerIdentity) {
          setState({ learnerIdentity });
        },
        onUpdateMessage(message) {
          setState({ message });
        },
      }}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          position: relative;
          width: 100%;
          height: 100%;
        `}
      >
        {!submitted && (
          <div
            css={css`
              width: 100%;
              height: 100%;
              opacity: 1;
              ${submitted &&
                `
            opacity: 0;
            transition: opacity 0.3s;
            `}
            `}
          >
            {!isTextEntry && include5StarRating && FiveStarList}
            {Id}
            {showTextEntry && (
              <TextEntry
                id={`text=-entry-${props.id}`}
                message={message}
                onUpdate={message => {
                  setState({ message });
                }}
                messageContainer={messageContainer}
                messageUnderline={messageUnderline}
                messageTextColor={messageTextColor}
                messageTextBackgroundColor={
                  `rgba(${r}, ${g}, ${b}, ${a})` || messageTextBackgroundColor
                }
                messageBorderRadius={messageBorderRadius}
                messageBorderColor={
                  messageBorder || messageUnderline
                    ? messageBorderColor
                    : 'transparent'
                }
                messageFontFamily={fontFamily}
                messageFontSize={messageFontSize}
                maxLinesBeforeScrolling={10}
                maxHeight={240}
              />
            )}
            {SubmitBtn}
          </div>
        )}

        {submitted &&
          (FeedbackSubmittedEditable ?? (
            <FeedbackSubmitted color={textColor} />
          ))}
      </div>
    </FiveStarRatingContext.Provider>
  );
};

export default FiveStarRating;
export { FiveStarRatingContext };
